import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { ICustomer, ILocation } from 'src/app/features/customers/models/customers.models';
import { Observable, Subscription } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { map, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { GetCatalogById, GetCatalogByIdError, GetCatalogByIdSuccess, ShareCatalog, ShareCatalogError, ShareCatalogSuccess } from '../../store/catalogs.actions';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { HasPermissionContext } from 'src/app/shared/has-permission.directive';

@Component({
  selector: 'app-share-catalog',
  templateUrl: './share-catalog.component.html',
  styleUrls: ['./share-catalog.component.scss']
})
export class ShareCatalogComponent implements OnInit, OnDestroy {
  isAttachmentEnabled = false;
  searchTerm = '';

  catalogLocations: ILocation[] = [];
  isLoading$: Observable<boolean> = this.store.select((state) => state.catalogs.loading);

  locations: ILocation[] = [];
  selectedLocations: number[] = [];
  catalogId: number;
  catalogName: string;
  errorMessage = '';
  canSubmit = false;

  private subscriptions: Subscription[] = [];

  locationsLoaded: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ShareCatalogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<AppState>,
    private actions$: Actions,
    private translateService: TranslateService
  ) {
    this.catalogId = data.catalogId;
    this.catalogName = data.catalogName;
  }

  ngOnInit(): void {
    // Fetch catalog to get associated locations
    this.store.dispatch(GetCatalogById({ id: this.catalogId }));

    const catalogByIdSuccess = this.actions$.pipe(
      ofType(GetCatalogByIdSuccess),
      tap((action) => {
        this.catalogLocations = action.catalog.companies?.flatMap((company: ICustomer) => company.locations?.map((location: ILocation) => ({
          ...location,
          company_name: company.name,
        })) || []) || [];

        this.locations = (this.catalogLocations ?? []).slice().sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        if (this.locations.length > 0 && this.selectedLocations.length == 0) {
          this.selectedLocations = this.locations.filter((location) => this.locationHasContactsWithEmails(location)).map((location) => location.id).filter((id): id is number => typeof id === 'number');
        }

        this.locationsLoaded = true;
    })).subscribe();

    const catalogLoadingError = this.actions$.pipe(
        ofType(GetCatalogByIdError),
      ).subscribe(() => {
        this.locationsLoaded = false;
      });

    const shareSub = this.actions$.pipe(
      ofType(ShareCatalog),
    ).subscribe(() => {
      this.errorMessage = '';
    });

    const shareSuccessSub = this.actions$.pipe(
      ofType(ShareCatalogSuccess),
      map(() => {
        this.dialogRef.close({
          success: true,
          notifiedCount: this.selectedLocations.length
        });
      })
    ).subscribe();

    const shareErrorSub = this.actions$.pipe(
      ofType(ShareCatalogError),
      map((action: any) => {
        this.errorMessage = action.error?.message || this.translateService.instant('Catalogs.share_catalog.error_message');
      })
    ).subscribe();

    const permissionSub = this.store.select(selectCanDo('catalog', 'edit')).subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
    });


    this.subscriptions.push(catalogByIdSuccess, catalogLoadingError, shareSub, shareSuccessSub, shareErrorSub, permissionSub);
  }

  handleNotify(): void {
    if (!this.canSubmit) return;
    if (this.selectedLocations.length === 0) {
      this.errorMessage = this.translateService.instant('Catalogs.share_catalog.select_location_error');
      return;
    }

    this.store.dispatch(ShareCatalog({
      catalogId: this.catalogId,
      data: {
        locations_ids: this.selectedLocations,
        attach_excel: this.isAttachmentEnabled
      }
    }));
  }

  handleCancel(): void {
    this.dialogRef.close();
  }

  toggleLocation(location: ILocation): void {
    if (!this.canSubmit) return;
    if (this.selectedLocations.includes(location.id!)) {
      this.selectedLocations = this.selectedLocations.filter(locId => locId !== location.id);
    } else if (this.locationHasContactsWithEmails(location)) {
      this.selectedLocations = [...this.selectedLocations, location.id!];
    }
  }

  get isAllSelected(): boolean {
    return this.locations.length > 0 && this.selectedLocations.length === this.locations.filter(location => this.locationHasContactsWithEmails(location)).length;
  }

  get isIndeterminate(): boolean {
    return this.selectedLocations.length > 0 && this.selectedLocations.length < this.locations.length;
  }

  get filteredLocations(): ILocation[] {
    const filterKey = this.searchTerm.trim().toLowerCase()
    return this.locations.filter(location =>
      location.name.trim().toLowerCase().includes(filterKey) ||
      location.company_name?.trim().toLowerCase().includes(filterKey)
    );
  }

  toggleAll(): void {
    if (!this.canSubmit) return;
    if (this.isAllSelected) {
      this.selectedLocations = [];
    } else {
      this.selectedLocations = this.locations.filter((location) => this.locationHasContactsWithEmails(location)).map(loc => loc.id!);
    }
  }

  locationHasContactsWithEmails(location: ILocation): boolean {
    return !!location.contacts_information.find(contact => !!contact.email);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  submitPermission(): HasPermissionContext {
    return { module: 'catalog', action: 'edit' };
  }
}

